import {animate, AnimationBuilder, AnimationPlayer, style} from '@angular/animations';
import {DOCUMENT} from '@angular/common';
import {Inject, Injectable, OnDestroy} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';

import {AuthHelperService} from 'app/auth/helpers/auth-helper.service';
import { IframeAbleService } from 'app/auth/helpers/iframeable.service';
import {AuthenticationService} from 'app/auth/service';
import {AuthFacade} from 'app/store/auth.facade';
import {of, Subscription} from 'rxjs';
import {delay, filter, take} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class CoreLoadingScreenService implements OnDestroy{
  loadingScreenEl: Element;
  animationPlayer: AnimationPlayer;
  loginScreenSubscription: Subscription;
  public _isThirdPartyLogin$ = this._authFacade$.isThirdPartyLogin$;

  /**
   * Constructor
   *
   * @param _document
   * @param {Router} _router
   * @param {AnimationBuilder} _animationBuilder
   * @param _authenticationService
   * @param authHelpder
   * @param _authFacade$
   */
  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private _router: Router,
    private _animationBuilder: AnimationBuilder,
    private _authenticationService: AuthenticationService,
    private authHelpder: AuthHelperService,
    private _authFacade$: AuthFacade,
    private iframeAbleService: IframeAbleService
  ) {

    // Initialize
    this._init();
  }

  // Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Initialize
   *
   * @private
   */
  _init(): void {
    const URLS_TO_IGNORE_LAYOUT_CONFIG = [
      '/sender/', 
      '/shareable/',
      '/onboarding',
      '/trail-period-completed', 
      '/download/',
      '/online/', 
      'error', 
      'export-pdf-view'
    ];
    // Get the loading screen element
    this.loadingScreenEl = this._document.body.querySelector('#loading-bg');

    // If loading screen element
    if (this.loadingScreenEl) {
      // Hide it on the first NavigationEnd event
      this._router.events
        .pipe(
          filter(event => event instanceof NavigationEnd),
          take(1)
        )
        .subscribe((event: NavigationEnd) => {
          this.loginScreenSubscription = of(null)
            .pipe(
              delay(1),
              take(1)
            ).subscribe(() => {
              this._authenticationService.isLoggedIn().then((login) => {
                if (event.url.indexOf('/s/') == -1) {
                  this._isThirdPartyLogin$.pipe(take(1)).subscribe(async (isThirdPartyLogin: boolean) => {
                    const isIframeableLogin = await this.iframeAbleService.isIframeAbleLoggedIn();
                    if (isThirdPartyLogin || isIframeableLogin) {
                      this.authHelpder.setLayoutConfig(true, false, false);
                    } else {
                      const shouldSetLayoutConfig = login && !URLS_TO_IGNORE_LAYOUT_CONFIG.some(urlPart => event.url.includes(urlPart));
                      this.authHelpder.setLayoutConfig(!shouldSetLayoutConfig, shouldSetLayoutConfig, shouldSetLayoutConfig);
                    }
                  })
                }
                this.hide();
              })
            })
        });
    }
  }

  // Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Show the loading screen
   */
  show(): void {
    this.animationPlayer = this._animationBuilder
      .build([
        style({
          opacity: '0',
          zIndex: '99999'
        }),
        animate('250ms ease', style({ opacity: '1' }))
      ])
      .create(this.loadingScreenEl);

    this.loginScreenSubscription = of(null)
      .pipe(
        delay(0),
        take(1)
      ).subscribe(() => {
        this.animationPlayer.play();
      });
  }

  /**
   * Hide the loading screen
   */
  hide(): void {
    this.animationPlayer = this._animationBuilder
      .build([
        style({ opacity: '1' }),
        animate(
          '250ms ease',
          style({
            opacity: '0',
            zIndex: '-10'
          })
        )
      ])
      .create(this.loadingScreenEl);

    this.loginScreenSubscription = of(null).
      pipe(
        delay(0),
        take(1)
      ).
      subscribe(() => {
        this.loadingScreenEl.remove();
        this.animationPlayer.play();
      });
  }



  ngOnDestroy() {
    if (this.loginScreenSubscription) {
      this.loginScreenSubscription.unsubscribe();
    }
  }
}
